import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { formatNewsDate, sentimentColor, sentimentLabel } from './newsUtils';

const SentimentPanel = ({ sentiment }) => (
  <div className="bg-gray-50 rounded-lg p-4 mb-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-gray-900">{sentiment.symbol} Sentiment</h3>
      <Badge className={sentimentColor(sentiment.sentiment_score)}>
        {sentiment.sentiment_score >= 0 ? (
          <TrendingUp size={12} className="mr-1" />
        ) : (
          <TrendingDown size={12} className="mr-1" />
        )}
        {sentimentLabel(sentiment.sentiment_score)}
      </Badge>
    </div>
    <div className="grid grid-cols-3 gap-4 text-sm">
      <div className="bg-green-50 p-3 rounded-lg text-center">
        <div className="text-green-700 font-bold text-xl">{sentiment.positive}%</div>
        <div className="text-green-600">Bullish</div>
      </div>
      <div className="bg-gray-100 p-3 rounded-lg text-center">
        <div className="text-gray-700 font-bold text-xl">{sentiment.neutral}%</div>
        <div className="text-gray-600">Neutral</div>
      </div>
      <div className="bg-red-50 p-3 rounded-lg text-center">
        <div className="text-red-700 font-bold text-xl">{sentiment.negative}%</div>
        <div className="text-red-600">Bearish</div>
      </div>
    </div>
    {sentiment.buzz?.articles_week > 0 && (
      <div className="mt-3 text-xs text-gray-500">
        {sentiment.buzz.articles_week} articles this week (avg: {sentiment.buzz.weekly_avg})
      </div>
    )}
  </div>
);

const CompanyNewsSearchCard = ({
  searchSymbol,
  setSearchSymbol,
  onSearch,
  loadingCompany,
  companyNews,
  sentiment,
}) => (
  <Card className="mb-8" data-testid="company-news-search">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <Search size={20} />
        Company News & Sentiment
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex gap-4 mb-4">
        <Input
          placeholder="Enter stock symbol (e.g., AAPL)"
          value={searchSymbol}
          onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          className="flex-1"
          data-testid="company-news-input"
        />
        <Button
          onClick={onSearch}
          disabled={loadingCompany || !searchSymbol.trim()}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="company-news-search-btn"
        >
          {loadingCompany ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
          <span className="ml-2">Search</span>
        </Button>
      </div>

      {sentiment?.available && <SentimentPanel sentiment={sentiment} />}

      {companyNews.length > 0 && (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {companyNews.map((item, idx) => (
            <div
              key={item.url || `${item.title}-${idx}`}
              className="border-b border-gray-100 pb-3 last:border-0"
            >
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-900 hover:text-purple-600 line-clamp-1"
              >
                {item.title}
              </a>
              <p className="text-sm text-gray-500 line-clamp-2 mt-1">{item.summary}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                <span>{item.source}</span>
                <span>•</span>
                <span>{formatNewsDate(item.datetime)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default CompanyNewsSearchCard;
